import { getExtensionValue } from "@medplum/core";
import { Observation, QuestionnaireResponse } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import { setExtensionValue } from "@/utils/medplum";

/**
 * Reads a non-empty string extension value from a QuestionnaireResponse or Observation.
 */
function readNonEmptyStringExtension(
  resource: QuestionnaireResponse | Observation | undefined,
  url: string,
): string | undefined {
  if (!resource) {
    return undefined;
  }
  const value = getExtensionValue(resource, url);
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Stamps which encounter QuestionnaireResponse `meta.versionId` this flowsheet QR was saved under.
 * Mirrors physical exam / newborn systems amend writes.
 */
export function applyQuestionnaireResponseEncounterParentVersionId(
  questionnaireResponse: QuestionnaireResponse,
  encounterQuestionnaireParentVersionId: string | undefined,
): QuestionnaireResponse {
  if (!encounterQuestionnaireParentVersionId) {
    const extension = (questionnaireResponse.extension ?? []).filter(
      (entry) => entry.url !== Extensions.QuestionnaireResponseParentVersionId,
    );
    return extension.length === (questionnaireResponse.extension ?? []).length
      ? questionnaireResponse
      : { ...questionnaireResponse, extension };
  }

  return {
    ...questionnaireResponse,
    extension: setExtensionValue(
      questionnaireResponse.extension,
      Extensions.QuestionnaireResponseParentVersionId,
      { valueString: encounterQuestionnaireParentVersionId },
    ),
  };
}

/**
 * Reads the encounter QuestionnaireResponse version id stamped on a flowsheet QR, if present.
 */
export function getQuestionnaireResponseEncounterParentVersionId(
  questionnaireResponse: QuestionnaireResponse | undefined,
): string | undefined {
  return readNonEmptyStringExtension(
    questionnaireResponse,
    Extensions.QuestionnaireResponseParentVersionId,
  );
}

/**
 * Reads the encounter QuestionnaireResponse version id stamped on a flowsheet observation, if present.
 */
export function getObservationEncounterAmendParentVersionId(
  observation: Observation | undefined,
): string | undefined {
  return readNonEmptyStringExtension(observation, Extensions.QuestionnaireResponseParentVersionId);
}
